import { Box, Grid, Skeleton } from "@mui/material";
import React, { useState, useEffect, useRef } from "react";

function TemplateSkeleton({ GridMdRowCount = 4 }) {
  const containerRef = useRef(null);
  const [cardCount, setCardCount] = useState(6);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const updateLayout = () => {
      if (containerRef.current) {
        const width = containerRef.current.offsetWidth;

        // Set responsive card count based on container width
        if (width < 500) {
          setCardCount(2);
          setIsMobile(true);
        } else if (width < 750) {
          setCardCount(3);
          setIsMobile(true);
        } else if (width < 960) {
          setCardCount(4);
          setIsMobile(false);
        } else if (width < 1280) {
          setCardCount(6);
          setIsMobile(false);
        } else {
          setCardCount(8);
          setIsMobile(false);
        }
      }
    };

    updateLayout();

    // Use ResizeObserver for continuous monitoring of container size changes
    const resizeObserver = new ResizeObserver(updateLayout);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      if (containerRef.current) {
        resizeObserver.unobserve(containerRef.current);
      }
    };
  }, []);

  const skeletonCards = Array(cardCount).fill(null);

  return (
    <Box
      ref={containerRef}
      sx={{
        width: "100%",
        p: { xs: "0.75rem", sm: "1rem", md: "1.25rem" },
      }}
    >
      <Grid
        container
        spacing={{ xs: 2, sm: 3, md: 4 }}
        sx={{
          flexDirection: isMobile ? "column" : "row",
          alignItems: isMobile ? "center" : "stretch"
        }}
      >
        {skeletonCards?.map((_, index) => (
          <Grid
            item
            xs={12}
            sm={isMobile ? 12 : 6}
            md={isMobile ? 12 : GridMdRowCount}
            key={index}
            sx={{
              width: isMobile ? "100%" : "auto",
              maxWidth: isMobile ? "450px" : "none"
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "0.5rem",
                height: "100%",
              }}
            >

              {/* Card content */}
              <Box
                sx={{
                  border: "1px solid #DADADA",
                  borderRadius: "0.625rem",
                  height: isMobile ? "10rem" : "12rem",
                  position: "relative",
                  width: "100%",
                  flexGrow: 1,
                }}
              >
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: "0.4rem", py:"0.5rem", px: "1rem" }}
                >
                  <Skeleton variant="circular" width={24} height={24} />
                  <Skeleton variant="text" width={120} height={24} />
                </Box>
                {/* Description area */}
                <Box sx={{ px: "1rem" }}>
                  <Skeleton variant="text" sx={{ fontSize: "0.9rem" }} />
                  <Skeleton variant="text" sx={{ fontSize: "0.9rem" }} />
                  <Skeleton
                    variant="text"
                    sx={{ fontSize: "0.9rem" }}
                    width="80%"
                  />
                </Box>

                {/* Practice area footer */}
                <Box
                  sx={{
                    position: "absolute",
                    bottom: 0,
                    width: "100%",
                    borderTop: "1px solid #DADADA",
                    height: "2rem",
                    display: "flex",
                    alignItems: "center",
                    gap: "0.4rem",
                    px: "0.5rem",
                  }}
                >
                  <Skeleton variant="text" width={140} height={20} />
                </Box>

                {/* Status badge */}
                <Box
                  sx={{
                    position: "absolute",
                    bottom: "2.2rem",
                    right: "0.2rem",
                  }}
                >
                  <Skeleton variant="rounded" width={40} height={20} />
                </Box>
              </Box>
            </Box>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}

export default TemplateSkeleton;